import PropTypes from 'prop-types';

export const tabTheme = {};

export const ScannerStatusEnum = {
  SCHEDULED: {
    key: 'scheduled',
    label: 'Scheduled',
    cssStyle: 'statusScheduled'
  },
  QUEUED: { key: 'queued', label: 'Queued', cssStyle: 'statusQueued' },
  RUNNING: { key: 'running', label: 'Running', cssStyle: 'statusRunning' },
  IMPORTING: {
    key: 'importing',
    label: 'Importing',
    cssStyle: 'statusImporting'
  },
  PENDING_REVIEW: {
    key: 'pending_review',
    label: 'Pending review',
    cssStyle: 'statusPendingReview'
  },
  STOPPED: { key: 'stopped', label: 'Stopped', cssStyle: 'statusStopped' },
  PAUSED: { key: 'paused', label: 'Paused', cssStyle: 'statusPaused' },
  DELETING: { key: 'deleting', label: 'Deleting', cssStyle: 'statusDeleting' },
  STALE: { key: 'stale', label: 'Stale', cssStyle: 'statusStale' },
  PARTIALLYDELETED: {
    key: 'partially_deleted',
    label: 'Partially Deleted',
    cssStyle: 'statusPartiallyDeleted'
  },
  // Fallback for scanner statuses not in UI enum
  UNKNOWN: { key: 'unknown', label: 'Unknown', cssStyle: 'statusUnknown' }
};

// Polling interval in milliseconds (10 seconds)
export const POLLING_INTERVAL_MS = 10000;

// Auth scheme that is only supported in the enhanced (Omni) experience.
// Scanners holding this scheme cannot be created or edited in the classic UI;
// users are directed to the enhanced experience instead.
export const OAUTH2_AUTH_CODE_SCHEME = 'oauth2-authorization-code';

/**
 * Returns the ScannerStatusEnum object for a given key string value
 * @param {string} key
 * @returns {object|ScannerStatusEnum.UNKNOWN}
 */
export const getScannerStatusByKey = (key) => {
  if (!key || typeof key !== 'string') {
    return ScannerStatusEnum.UNKNOWN;
  }
  const normalizedKey = key.toLowerCase();
  const statusScannerInfo = Object.values(ScannerStatusEnum).find(
    (statusInfo) => statusInfo.key.toLowerCase() === normalizedKey
  );

  // Return UNKNOWN status as fallback if not found
  return statusScannerInfo || ScannerStatusEnum.UNKNOWN;
};

export const RunStatusEnum = {
  COMPLETED: {
    key: 'completed',
    label: 'Completed',
    cssStyle: 'statusRunCompleted'
  },
  PENDING_REVIEW: {
    key: 'pending_review',
    label: 'Pending Review',
    cssStyle: 'statusRunPendingReview'
  },
  PARTIAL_SUCCESS: {
    key: 'partialsuccess',
    label: 'Partial Success',
    cssStyle: 'statusRunPartialSuccess'
  },
  CAPACITY_EXCEEDED: {
    key: 'capacity_exceeded',
    label: 'Capacity Exceeded',
    cssStyle: 'statusRunCapacityExceeded'
  },
  FAILED: { key: 'failed', label: 'Failed', cssStyle: 'statusRunFailed' },
  ABORTED: { key: 'aborted', label: 'Aborted', cssStyle: 'statusRunAborted' },
  // Extended statuses
  RUNNING: { key: 'running', label: 'Running', cssStyle: 'statusRunRunning' },
  IMPORTING: {
    key: 'importing',
    label: 'Importing',
    cssStyle: 'statusRunImporting'
  },
  STALE: { key: 'stale', label: 'Stale', cssStyle: 'statusRunStale' },
  // Fallback for run statuses not in UI enum
  UNKNOWN: { key: 'unknown', label: 'Unknown', cssStyle: 'statusRunUnknown' }
};

/**
 * Returns the RunStatusEnum object for a given key string value
 * @param {string} key
 * @returns {object|RunStatusEnum.UNKNOWN}
 */
export const getRunStatusByKey = (key) => {
  if (!key || typeof key !== 'string') {
    return RunStatusEnum.UNKNOWN;
  }
  const normalizedKey = key.toLowerCase();
  const statusRunInfo = Object.values(RunStatusEnum).find(
    (statusInfo) => statusInfo.key.toLowerCase() === normalizedKey
  );

  // Return UNKNOWN status as fallback if not found
  return statusRunInfo || RunStatusEnum.UNKNOWN;
};

export const filterOptions = (() => {
  const options = [{ value: 'any', label: 'All statuses' }];

  for (const scannerStatusInfo of Object.values(ScannerStatusEnum)) {
    // e.g. expected option to add: { value: 'scheduled', label: 'Scheduled' }
    options.push({
      value: scannerStatusInfo.key,
      label: scannerStatusInfo.label
    });
  }

  return options;
})();

export const ScanConfigurationPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  connection: PropTypes.object.isRequired,
  pendingActions: PropTypes.number,
  creationDate: PropTypes.string
});

export const dateFormat = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric'
};

// Number of items per page in the scan configurations list (pagination)
export const ITEMS_PER_PAGE = 8;

export const PROVIDER_LABELS = {
  aws: 'Amazon',
  microsoft: 'Microsoft',
  google: 'Google',
  godaddy: 'GoDaddy',
  kong: 'Kong'
};

export const ASSET_TYPE_LABELS = {
  agent: 'Agent',
  mcp: 'MCP',
  api: 'API',
  rest_api: 'REST API'
};

export const FORM_ASSET_TYPE_LABELS = {
  agent: 'Agents',
  mcp: 'MCPs',
  api: 'APIs',
  rest_api: 'REST APIs'
};

// Shared PropTypes for scanner-related components
export const scannerPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  connectionId: PropTypes.string,
  state: PropTypes.oneOf(['RUNNING', 'IDLE', 'COMPLETED', 'DISABLED']),
  status: PropTypes.oneOf([
    'SUCCESS',
    'CLOSE',
    'RUNNING',
    'IDLE',
    'ERROR',
    'COMPLETED'
  ]),
  lastRunAt: PropTypes.string,
  lastRunStatus: PropTypes.string,
  lastSuccessfulRunAt: PropTypes.string,
  lastSuccessfulRunSummary: PropTypes.shape({
    status: PropTypes.string,
    description: PropTypes.string,
    stats: PropTypes.shape({
      added: PropTypes.number,
      updated: PropTypes.number,
      removed: PropTypes.number,
      unchanged: PropTypes.number
    })
  }),
  createdAt: PropTypes.string,
  nextRunAt: PropTypes.string,
  parameters: PropTypes.any,
  runPolicy: PropTypes.any,
  notes: PropTypes.string,
  schedule: PropTypes.string,
  connection: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    authScheme: PropTypes.string,
    status: PropTypes.string,
    authParameters: PropTypes.shape({
      region: PropTypes.string,
      type: PropTypes.string
    }),
    targetSystem: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string
    })
  })
});

export const scannerRunPropType = PropTypes.shape({
  id: PropTypes.string,
  status: PropTypes.string,
  startedAt: PropTypes.string,
  endedAt: PropTypes.string,
  createdAt: PropTypes.string,
  summary: PropTypes.shape({
    added: PropTypes.number,
    updated: PropTypes.number,
    removed: PropTypes.number,
    unchanged: PropTypes.number,
    issuesFound: PropTypes.arrayOf(PropTypes.string)
  })
});
